import { ErrorBoundary } from '@src/components/ErrorBoundary';
import {
  OTC_MISSING_DEPENDENCIES_LOCATION,
  OTCMissingDependenciesBanner,
} from '@src/components/OTC/MissingDependencies';
import type { DealBookingType, OTCFieldsData } from '@src/types/OTC';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { Box, HStack, useDisclosureWithContext } from '@talos/kyoko';
import { memo, useCallback, type ComponentType } from 'react';
import type { DealProductBuilderPanelConfig } from '../../types/LayoutConfig';
import { DealProductBuilderActions } from './DealProductBuilderActions';
import { DealProductBuilderAssetsProvider } from './DealProductBuilderAssetsProvider';
import { DealProductBuilderDetails } from './DealProductBuilderDetails';
import { FixedRateLoanProductBuilderGrid } from './DealProductBuilderGrid/FixedRateLoanProductBuilderGrid';
import { FloatingRateLoanProductBuilderGrid } from './DealProductBuilderGrid/FloatingRateLoanProductBuilderGrid';
import { ForwardProductBuilderGrid } from './DealProductBuilderGrid/ForwardProductBuilderGrid';
import { OptionProductBuilderGrid } from './DealProductBuilderGrid/OptionProductBuilderGrid';
import { RepoProductBuilderGrid } from './DealProductBuilderGrid/RepoProductBuilderGrid';
import { DealProductBuilderHeaderSelector } from './DealProductBuilderHeaderSelector';
import { DealProductBuilderProvider } from './DealProductBuilderProvider';
import { useDealProductBuilder } from './DealProductBuilderProvider/useDealProductBuilder';
import { OTCFieldsDialog } from './OTCFieldsDialog';
import { getProductDisplayName } from './productConfig';
import { DealProductBuilderWrapper } from './styles';

interface DealProductBuilderProps {
  bookingType: DealBookingType;
  resolvedSymbol: string | undefined;
  panelID: string;
  updateDealProductBuilderPanelData: (panelData: Partial<DealProductBuilderPanelConfig>, label: string) => void;
}

type DealProductBuilderBodyProps = Pick<DealProductBuilderProps, 'panelID' | 'bookingType'>;

interface GridComponentProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

const productTypeMap = {
  forward: {
    dialogTitle: 'OTC Fields',
    Grid: ForwardProductBuilderGrid,
  },
  fixedRateLoan: {
    dialogTitle: 'Loan Fields',
    Grid: FixedRateLoanProductBuilderGrid,
  },
  option: {
    dialogTitle: 'Option Fields',
    Grid: OptionProductBuilderGrid,
  },
  floatingRateLoan: {
    dialogTitle: 'Loan Fields',
    Grid: FloatingRateLoanProductBuilderGrid,
  },
  repo: {
    dialogTitle: 'Repo Fields',
    Grid: RepoProductBuilderGrid,
  },
} as const satisfies Record<DealBookingType, { dialogTitle: string; Grid: ComponentType<GridComponentProps> }>;

export const DealProductBuilderBody = memo(({ panelID, bookingType }: DealProductBuilderBodyProps) => {
  const productDetailsDisclosure = useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>();
  const {
    bookingType: currentBookingType,
    state,
    dispatch,
    actions: { updateOTCFields, clearError },
  } = useDealProductBuilder();

  const handleOTCFieldsSave = useCallback(
    (otcFields: OTCFieldsData) => {
      if (productDetailsDisclosure.context) {
        dispatch(clearError());
        dispatch(updateOTCFields({ rowIndex: productDetailsDisclosure.context.rowIndex, otcFields }));
      }
      productDetailsDisclosure.close();
    },
    [productDetailsDisclosure, dispatch, clearError, updateOTCFields]
  );

  const { dialogTitle, Grid } = productTypeMap[bookingType];

  return (
    <>
      <HStack justifyContent="space-between" mb={1} background="colors.gray.020" gap="spacing12" p="spacing8">
        <DealProductBuilderDetails panelID={panelID} />
      </HStack>

      <OTCMissingDependenciesBanner
        location={OTC_MISSING_DEPENDENCIES_LOCATION.DealProductBuilder}
        bookingType={currentBookingType}
      />

      <Grid panelID={panelID} productDetailsDisclosure={productDetailsDisclosure} />

      <DealProductBuilderActions
        rowData={state.rowData}
        bookingType={currentBookingType}
        isFormValid={state.isFormValid}
      />

      <OTCFieldsDialog title={dialogTitle} disclosure={productDetailsDisclosure} onSave={handleOTCFieldsSave} />
    </>
  );
});

export const DealProductBuilderContent = memo(
  ({ resolvedSymbol: symbol, panelID, updateDealProductBuilderPanelData }: DealProductBuilderProps) => {
    const { bookingType: currentBookingType } = useDealProductBuilder();

    const handleBookingTypeChangeForShell = useCallback(
      (newBookingType: DealBookingType) => {
        updateDealProductBuilderPanelData(
          { bookingType: newBookingType, resolvedSymbol: symbol },
          getProductDisplayName(newBookingType)
        );
      },
      [symbol, updateDealProductBuilderPanelData]
    );

    return (
      <DealProductBuilderWrapper data-testid="deal-product-builder-wrapper">
        <Box p="spacing8" background="backgroundContent">
          <DealProductBuilderHeaderSelector panelID={panelID} onBookingTypeChange={handleBookingTypeChangeForShell} />
        </Box>
        <DealProductBuilderBody panelID={panelID} bookingType={currentBookingType} />
      </DealProductBuilderWrapper>
    );
  }
);

const DealProductBuilder = memo(({ bookingType, ...props }: DealProductBuilderProps) => {
  return (
    <ErrorBoundary>
      <DealProductBuilderAssetsProvider>
        <DealProductBuilderProvider initialBookingType={bookingType}>
          <DealProductBuilderContent bookingType={bookingType} {...props} />
        </DealProductBuilderProvider>
      </DealProductBuilderAssetsProvider>
    </ErrorBoundary>
  );
});
export default DealProductBuilder;
